import { QuestionRulesConditions } from '@app/src/components/Drawer/Views/DrawerViewAddQuestionRule'
import useQuestionRules from '@app/src/hooks/useQuestionRules'
import RequestItemContent from '@app/src/pages/Questionnaire/Provider/Report/RequestItemContent'
import { useReport } from '@app/src/pages/Questionnaire/ReportContext'
import RequestItemTitle from '@app/src/pages/Questionnaire/RequestItemTitle'
import type {
  QuestionRule,
  Request,
  RequestItem as RequestItemType,
  ResponseItemWithPreviousCorrectionNeededItem,
} from '@app/src/types/resourceExplorer'
import { AnswerStatus } from '@app/src/types/resourceExplorer'
import { QuestionTypes } from '@app/src/wf-constants'
import { Box } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

interface RequestItemProps {
  requestItem: RequestItemType
  responseItem?: ResponseItemWithPreviousCorrectionNeededItem
  sectionNumber: number
  itemNumber: number
}

interface RuleFields {
  ruleId: string | number
  triggerQuestionId: number
  fieldNames: string[]
}

const buildRuleFields = (request: Request, rule: QuestionRule): RuleFields | null => {
  const triggerRequestItem = request.sections
    .flatMap(section => section.items)
    .find(item => item.questionId === rule.questionId)

  if (!triggerRequestItem) return null

  const fieldNames = [
    `${triggerRequestItem.id}.selectedOptions`,
    `${triggerRequestItem.id}.cannotAnswer`,
    `${triggerRequestItem.id}.answerStatus`,
  ]

  if (rule.conditionValue === QuestionRulesConditions.FileUploaded) {
    fieldNames.push(`${triggerRequestItem.id}.uploadReferences.originalFileName`)
  }

  return {
    ruleId: rule.id || `${rule.questionId}-${rule.conditionValue}`,
    triggerQuestionId: rule.questionId,
    fieldNames,
  }
}

const RequestItem: React.FC<RequestItemProps> = ({ requestItem, responseItem, sectionNumber, itemNumber }) => {
  const { requestItemsRules, request } = useReport()
  const { applySkipRuleEffectsToQuestion } = useQuestionRules(requestItem)
  const { watch, getValues, control } = useFormContext()

  const requestItemValue = useWatch({
    control,
    name: `${requestItem.id}`,
    defaultValue: getValues(`${requestItem.id}`),
  })

  const isExcluded = useMemo(() => {
    return requestItemValue?.answerStatus === AnswerStatus.Excluded
  }, [requestItemValue])

  const [affectingRules, setAffectingRules] = useState<RuleFields[]>([])

  useEffect(() => {
    if (!requestItemsRules?.length || !request?.sections) return

    const rules = requestItemsRules
      .filter(rule => rule.sectionQuestionTargets?.some(target => target.targetQuestionId === requestItem.questionId))
      .map(rule => buildRuleFields(request, rule))
      .filter(Boolean) as RuleFields[]

    setAffectingRules(rules)
  }, [requestItemsRules, request?.sections, requestItem.questionId])

  const watchedFields = useMemo(() => {
    const fields = affectingRules.flatMap(rule => rule.fieldNames)
    return fields
  }, [affectingRules, requestItem.questionId])

  const fieldValues = watch(watchedFields)

  useEffect(() => {
    if (!affectingRules.length || !request?.sections) return

    affectingRules.forEach(({ triggerQuestionId }) => {
      const triggerRequestItem = request.sections
        .flatMap(section => section.items)
        .find(item => item.questionId === triggerQuestionId)

      if (triggerRequestItem) {
        const cannotAnswerValue = getValues(`${triggerRequestItem.id}.cannotAnswer`)
        const selectedValue = getValues(`${triggerRequestItem.id}.selectedOptions`)
        const hasFileUploaded = Boolean(getValues(`${triggerRequestItem.id}.uploadReferences.originalFileName`))

        if (cannotAnswerValue === true) {
          applySkipRuleEffectsToQuestion(triggerQuestionId, [QuestionRulesConditions.Skipped])
        } else if (hasFileUploaded && triggerRequestItem.questionType?.name === QuestionTypes.File) {
          applySkipRuleEffectsToQuestion(triggerQuestionId, [QuestionRulesConditions.FileUploaded])
        } else {
          applySkipRuleEffectsToQuestion(triggerQuestionId, selectedValue)
        }
      }
    })
  }, [
    affectingRules,
    applySkipRuleEffectsToQuestion,
    getValues,
    request?.sections,
    fieldValues,
    requestItem.questionId,
  ])

  return (
    <Box
      data-pdf-block
      mt={5}
      sx={({ palette }) => ({ border: `1px solid ${palette.grey[300]}`, borderRadius: 2, overflow: 'hidden' })}
    >
      <RequestItemTitle
        item={requestItem}
        sectionNumber={sectionNumber}
        itemNumber={itemNumber}
        responseItem={responseItem}
        excluded={isExcluded}
      />
      {!isExcluded && (
        <Box flexGrow={1} data-testid="request">
          <RequestItemContent
            item={requestItem}
            responseItem={responseItem}
            sectionNumber={sectionNumber}
            itemNumber={itemNumber}
          />
        </Box>
      )}
    </Box>
  )
}

export default RequestItem
